// src/pages/PreLoginGate.jsx
import * as React from "react";
import { Box, Paper, Typography, TextField, Button, Stack, Fade } from "@mui/material";

const TARGET_URL = "/login";
const CORRECT_CODE = "113322";

const asset = (p) => (process.env.PUBLIC_URL || "") + p;

const BACKGROUNDS = [
  asset("/assets/bg1.jpg"),
  asset("/assets/bg2.jpg"),
  asset("/assets/bg3.jpg"),
  asset("/assets/bg4.jpg"),
  asset("/assets/bg5.jpg"),
];

const LOGO_URL = asset("/assets/logo.png");

export default function PreLoginGate() {
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState("");
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(
      () => setIndex((prev) => (prev + 1) % BACKGROUNDS.length),
      5000
    );
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim() === CORRECT_CODE) {
      window.location.href = TARGET_URL;
    } else {
      setError("That code doesn’t look right.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        px: 2,
      }}
    >
      {/* Background slideshow - light tint */}
      {BACKGROUNDS.map((bg, i) => (
        <Fade in={i === index} timeout={1500} key={bg}>
          <Box
            sx={{
              backgroundImage: `linear-gradient(rgba(0,0,0,.08), rgba(0,0,0,.18)), url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "absolute",
              inset: 0,
              zIndex: -2,
            }}
          />
        </Fade>
      ))}

      {/* Subtle overlay for clarity */}
      <Box sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.03)", zIndex: -1 }} />

      {/* Logo + smaller card */}
      <Stack spacing={{ xs: 4.5, sm: 5.5 }} alignItems="center" sx={{ width: "min(92vw, 600px)" }}>
        {/* Logo stays prominent */}
        <Box
          component="img"
          src={LOGO_URL}
          alt="Muse — Flexicity Management Platform"
          sx={{
            height: { xs: 160, sm: 180, md: 200 },
            width: "auto",
            userSelect: "none",
            pointerEvents: "none",
            filter: "drop-shadow(0 8px 18px rgba(0,0,0,.45))",
          }}
        />

        {/* More transparent, smaller card */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 480,
            borderRadius: 3,
            p: 0.6,
            background:
              "linear-gradient(145deg, rgba(255,255,255,.28), rgba(255,255,255,.08))",
            boxShadow:
              "0 10px 30px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.15)",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              p: { xs: 2.5, sm: 4 },
              textAlign: "center",
              color: "white",
              bgcolor: "rgba(255,255,255,0.035)", // more transparent
              border: "1px solid rgba(255,255,255,0.25)",
              backdropFilter: "saturate(160%) blur(20px)",
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,.05), 0 8px 22px rgba(0,0,0,.18)",
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5} alignItems="center">
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textShadow: "0 2px 6px rgba(0,0,0,.25)",
                  }}
                >
                  Enter access code
                </Typography>

                <TextField
                  variant="filled"
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (error) setError("");
                  }}
                  fullWidth
                  autoFocus
                  InputProps={{
                    sx: {
                      textAlign: "center",
                      "& input": {
                        textAlign: "center",
                        fontWeight: 700,
                        letterSpacing: 3,
                        color: "#0f1115",
                      },
                      "& input::placeholder": {
                        color: "rgba(17,17,21,0.55)",
                        opacity: 1,
                      },
                    },
                  }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 12 }}
                  sx={{
                    mx: "auto",
                    maxWidth: 360,
                    "& .MuiFilledInput-root": {
                      backgroundColor: "rgba(245,247,250,0.74)",
                      borderRadius: 2,
                      boxShadow: "0 2px 10px rgba(0,0,0,.05) inset",
                      transition: "background-color .2s ease, box-shadow .2s ease",
                    },
                    "& .MuiFilledInput-root.Mui-focused": {
                      backgroundColor: "rgba(255,255,255,0.9)",
                      boxShadow: "0 4px 14px rgba(0,0,0,.08)",
                    },
                  }}
                  error={Boolean(error)}
                  helperText={error || " "}
                />

                <Button
                  type="submit"
                  size="large"
                  disabled={!code.trim()}
                  sx={{
                    mx: "auto",
                    width: "min(100%, 360px)",
                    py: 1.05,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    color: "#0f1115",
                    background: "rgba(255,255,255,0.88)",
                    border: "1px solid rgba(255,255,255,.55)",
                    backdropFilter: "blur(8px)",
                    boxShadow: "0 10px 26px rgba(0,0,0,.18)",
                    transition: "all .2s ease",
                    "&:hover": {
                      background: "rgba(255,255,255,0.96)",
                      boxShadow: "0 12px 30px rgba(0,0,0,.22)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(0,0,0,.45)",
                      background: "rgba(255,255,255,0.5)",
                      borderColor: "rgba(255,255,255,.4)",
                    },
                  }}
                >
                  Continue
                </Button>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}
